<template>
  <div class="gallery-container" v-if="hasImages">
    <!-- 主图 -->
    <div
      class="main-image-wrapper"
      :style="{ aspectRatio: mediaAspectRatio }"
      @touchstart="handleTouchStart"
      @touchend="handleTouchEnd"
    >
      <transition :name="transitionName">
        <div :key="currentIndex" class="image-container">
          <img
            :src="currentMainImage"
            class="main-image"
            :alt="images[currentIndex]?.title || ''"
          />
          <div class="image-title">
            {{ images[currentIndex]?.title || "" }}
          </div>
        </div>
      </transition>

      <div class="desktop-controls">
        <button class="nav-btn prev" @click="switchImage(-1)">&lt;</button>
        <button class="nav-btn next" @click="switchImage(1)">&gt;</button>
      </div>
    </div>

    <!-- 缩略图 -->
    <div class="thumbnail-list" ref="thumbList">
      <div
        v-for="(img, index) in images"
        :key="index"
        :ref="setThumbRef"
        :class="['thumbnail-item', { active: currentIndex === index }]"
        @click="switchTo(index)"
      >
        <img :src="currentThumbnail(img)" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductGallery",
  props: {
    images: { type: Array, required: true },
  },
  data() {
    return {
      currentIndex: 0,
      transitionName: "slide-next",
      windowWidth: window.innerWidth,
      touchStartX: 0,
      thumbRefs: [],
    };
  },
  computed: {
    hasImages() {
      return this.images.length > 0;
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    mediaAspectRatio() {
      return this.isMobile ? "3/4" : "5/3";
    },
    currentMainImage() {
      const img = this.images[this.currentIndex];
      return this.isMobile ? img.largeMobile : img.large;
    },
  },
  methods: {
    switchImage(step) {
      this.transitionName = step > 0 ? "slide-next" : "slide-prev";
      this.currentIndex =
        (this.currentIndex + step + this.images.length) % this.images.length;
      this.scrollToActiveThumb();
    },
    switchTo(index) {
      this.transitionName =
        index > this.currentIndex ? "slide-next" : "slide-prev";
      this.currentIndex = index;
      this.scrollToActiveThumb();
    },
    currentThumbnail(img) {
      return this.isMobile ? img.smallMobile : img.small;
    },
    handleTouchStart(e) {
      if (this.isMobile) this.touchStartX = e.touches[0].clientX;
    },
    handleTouchEnd(e) {
      if (!this.isMobile) return;
      const diff = e.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(diff) > 50) this.switchImage(diff > 0 ? -1 : 1);
    },
    scrollToActiveThumb() {
      this.$nextTick(() => {
        const el = this.thumbRefs[this.currentIndex];
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        }
      });
    },
    setThumbRef(el) {
      if (el) this.thumbRefs.push(el);
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },
  },
  mounted() {
    window.addEventListener("resize", this.updateWindowWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateWindowWidth);
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

.gallery-container {
  background: @color-bg-main;
}

.main-image-wrapper {
  position: relative;
  overflow: hidden;
  .cardRadius();
  background: @color-bg-gray;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .main-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  .image-title {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 10px;
    font-size: 1.2rem;
  }
}

.desktop-controls {
  .mobile-only({ display: none; });
  .nav-btn {
    .btnReset();
    border-radius: 50%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    background: rgba(@color-bg-main, 0.45);
    color: @color-text-title;
    font-size: 1.5rem;
    transition: all @ease-out 0.3s;
    &:hover {
      background: rgba(@color-bg-main, 0.75);
      color: @color-primary;
      font-weight: 700;
      transform: translateY(-50%) scale(1.1);
    }
    &.prev {
      left: 20px;
    }
    &.next {
      right: 20px;
    }
  }
}

.thumbnail-list {
  display: flex;
  gap: 12px;
  padding: 20px 0;
  overflow-x: auto;
  .thumbnail-list > div {
    flex-shrink: 0;
  }
  .thumbnail-item {
    flex: 0 0 100px;
    height: 75px;
    border: 2px solid transparent;
    .cardRadius();
    cursor: pointer;
    transition: all @ease-out 0.3s;
    &.active {
      border-color: @color-active;
      transform: scale(1.05);
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

/* 过渡动画 */
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: transform 0.4s @ease-out;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.slide-next-enter {
  transform: translateX(100%);
}
.slide-next-leave-to {
  transform: translateX(-100%);
}
.slide-prev-enter {
  transform: translateX(-100%);
}
.slide-prev-leave-to {
  transform: translateX(100%);
}
</style>
